import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatDetail } from "@/lib/api";
import { toast } from "sonner";

const STATS = [
  { key: "strength", label: "Strength", color: "text-red-400" },
  { key: "discipline", label: "Discipline", color: "text-amber-400" },
  { key: "focus", label: "Focus", color: "text-cyan-400" },
  { key: "knowledge", label: "Knowledge", color: "text-violet-400" },
  { key: "health", label: "Health", color: "text-emerald-400" },
];

export default function HabitEditor({ open, onOpenChange, habit, onSaved }) {
  const isEdit = !!habit;
  const [name, setName] = useState("");
  const [xp, setXp] = useState(20);
  const [stat, setStat] = useState("discipline");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (open) {
      setName(habit?.name ?? "");
      setXp(habit?.xp ?? 20);
      setStat(habit?.stat ?? "discipline");
      setConfirmDel(false);
    }
  }, [open, habit]);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/habits/${habit.key}`, { name: name.trim(), xp: Number(xp), stat });
        toast.success("Quest updated");
      } else {
        await api.post("/habits", { name: name.trim(), xp: Number(xp), stat });
        toast.success("New quest forged");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!isEdit) return;
    if (!confirmDel) {
      setConfirmDel(true);
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/habits/${habit.key}`);
      toast.success("Quest archived");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Archive failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!bg-[#0F0F16] !border-cyan-400/30 !rounded-none max-w-md"
        data-testid="habit-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle asChild>
            <div>
              <div className="label-tech mb-1">{isEdit ? "Modify Quest" : "New Quest"}</div>
              <div className="h-display text-2xl">
                {isEdit ? habit.name : "Forge Quest"}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <label className="label-tech block mb-1.5">Quest Name</label>
            <input
              data-testid="habit-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cold Shower"
              maxLength={60}
              autoFocus
              className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2.5 text-white text-sm font-mono-tech"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tech block mb-1.5">XP Reward</label>
              <input
                data-testid="habit-xp-input"
                type="number"
                min={1}
                max={200}
                value={xp}
                onChange={(e) => setXp(e.target.value)}
                className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2.5 text-white text-sm font-mono-tech"
              />
            </div>
            <div>
              <label className="label-tech block mb-1.5">Attribute</label>
              <select
                data-testid="habit-stat-select"
                value={stat}
                onChange={(e) => setStat(e.target.value)}
                className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2.5 text-white text-sm font-mono-tech uppercase"
              >
                {STATS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {habit?.custom === false && (
            <p className="text-[0.65rem] text-slate-500 font-mono-tech uppercase tracking-wider">
              Default quest — you can edit or archive it.
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              data-testid="habit-save-btn"
              type="submit"
              disabled={busy}
              className="btn-neon flex-1"
            >
              {busy ? "..." : isEdit ? "Save Changes" : "Forge Quest"}
            </button>
            {isEdit && (
              <button
                data-testid="habit-delete-btn"
                type="button"
                onClick={doDelete}
                disabled={busy}
                className={`btn-purple ${confirmDel ? "!border-red-500 !text-red-400" : ""}`}
              >
                {confirmDel ? "Confirm?" : "Archive"}
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
